import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { Button, Divider, Form, Input, Spin, Typography, Alert } from 'antd';
import { motion, AnimatePresence } from "framer-motion";
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../Hooks/useAuth';
import supabase, { getAuthRedirectURL } from '../../config/supabaseConfig';
import { INVALID_CREDENTIALS_MESSAGE } from '../../utils/authHelpers';
import { FcGoogle } from 'react-icons/fc';

const { Title, Paragraph } = Typography;

// Update the LoginFormValues interface
interface LoginFormValues {
    email: string;
    password: string;
    error: string | JSX.Element;
    loading: boolean;
}

interface LoginProps {
    handleToggleForm?: () => void;
    closeModal?: () => void;
    setForgotPassword?: () => void;
}

export const Login: React.FC<LoginProps> = ({ handleToggleForm, closeModal, setForgotPassword }) => {
    const { signIn, signInWithGoogle } = useAuth();
    const [values, setValues] = useState<LoginFormValues>({
        email: '',
        password: '',
        error: "",
        loading: false,
    });
    const navigate = useNavigate();
    const location = useLocation();

    const { email, password, error, loading } = values;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        setValues({ ...values, [e.target.name]: e.target.value });

    const handleResendVerification = async (email: string) => {
        try {
            setValues({ ...values, loading: true });

            const { error } = await supabase.auth.resend({
                type: 'signup',
                email: email,
                options: {
                    emailRedirectTo: getAuthRedirectURL('auth/callback')
                }
            });

            if (error) {
                setValues({
                    ...values,
                    loading: false,
                    error: `Erreur lors de l'envoi: ${error.message}`
                });
                return;
            }

            setValues({
                ...values,
                loading: false,
                error: (
                    <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="bg-green-50 border border-green-100 rounded-lg p-4 text-center text-green-700"
                    >
                        Un nouvel email de vérification a été envoyé. Veuillez vérifier votre boîte de réception.
                    </motion.div>
                )
            });

        } catch (error: unknown) {
            const err = error as { message: string };
            setValues({
                ...values,
                loading: false,
                error: err.message || "Erreur lors de l'envoi de l'email de vérification. Veuillez réessayer."
            });
        }
    };

    const handleSubmit = async () => {
        if (!email || !password) {
            setValues({ ...values, error: "Tous les champs sont obligatoires" });
            return;
        }

        setValues({ ...values, error: "", loading: true });

        try {
            await signIn(email, password);
            setValues({
                email: "",
                password: "",
                error: "",
                loading: false
            });

            closeModal?.();

            if (location.state?.from) {
                navigate(location.state.from.pathname);
            } else {
                navigate("/", { replace: true });
            }
        } catch (error: unknown) {
            const err = error as { message: string };
            
            // Check for email confirmation error - look for different possible error messages
            if (err.message === "Email not confirmed" || 
                err.message.includes("Email not confirmed") || 
                err.message.includes("email that is not confirmed") || 
                err.message.includes("Please verify your email")) {
                setValues({
                    ...values,
                    loading: false,
                    error: (
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-center"
                        >
                            <Alert
                                message="Email non vérifié"
                                description="Veuillez vérifier votre email pour activer votre compte."
                                type="warning"
                                showIcon
                                className="mb-3 text-left"
                            />
                            <Button
                                type="primary"
                                onClick={() => handleResendVerification(email)}
                                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                            >
                                Renvoyer l'email de vérification
                            </Button>
                        </motion.div>
                    )
                });
                return;
            }

            const isInvalidCredentials =
                err.message === 'Invalid login credentials' ||
                err.message.includes('Invalid login credentials');

            setValues({
                ...values,
                error: isInvalidCredentials ? INVALID_CREDENTIALS_MESSAGE : (err.message || "Erreur de connexion"),
                loading: false
            });
        }
    };

    const handleGoogleLogin = async () => {
        try {
            setValues({ ...values, loading: true });
            await signInWithGoogle();
            // Remove premature navigation - AuthCallback will handle the redirect
            closeModal?.();
        } catch (error: unknown) {
            const err = error as { message: string };
            setValues({
                ...values,
                error: err.message || "Erreur de connexion avec Google",
                loading: false
            });
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mt-6 min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100"
        >
            <motion.div
                className="max-w-[90rem] mx-auto flex flex-col md:flex-row shadow-xl rounded-2xl overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* Left side - Gradient background with illustration */}
                <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-blue-600 to-purple-600 p-12 justify-center items-center">
                    <div className="text-white max-w-md">
                        <motion.h1 
                            className="text-4xl font-bold mb-6"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 }}
                        >
                            Bienvenue sur Falashop
                        </motion.h1>
                        <motion.p 
                            className="text-xl opacity-90 mb-8"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            Connectez-vous pour accéder à votre compte et découvrir des milliers d'annonces.
                        </motion.p>
                        <motion.div 
                            className="bg-white/20 p-6 rounded-xl backdrop-blur-sm"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                        >
                            <p className="text-lg font-medium mb-4">Avec Falashop, vous pouvez :</p>
                            <motion.ul className="space-y-2">
                                {[
                                    "Publier des annonces gratuitement",
                                    "Discuter avec les vendeurs",
                                    "Sauvegarder vos annonces préférées"
                                ].map((item, index) => (
                                    <motion.li 
                                        key={index}
                                        className="flex items-center"
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.5 + (index * 0.1) }}
                                    >
                                        <span className="mr-2 text-xl">✓</span>
                                        <span>{item}</span>
                                    </motion.li>
                                ))}
                            </motion.ul>
                        </motion.div>
                    </div>
                </div>

                {/* Right side - Login form */}
                <div className="w-full md:w-1/2 p-8 md:p-12 flex items-center justify-center bg-white">
                    <div className="w-full max-w-md">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <Title level={2} className="mb-2">Connexion</Title>
                            <Paragraph className="text-gray-600 mb-8">
                                Entrez vos identifiants pour accéder à votre compte
                            </Paragraph>
                        </motion.div>

                        <Form
                            layout='vertical'
                            name="normal_login"
                            className="login-form"
                            initialValues={{ remember: true }}
                            onFinish={handleSubmit}
                            requiredMark='optional'
                            size="large"
                        >
                            <Form.Item
                                label={<label className='text-gray-700 font-medium'>Email</label>}
                                name="email"
                                rules={[{ required: true, message: 'Veuillez entrer votre email!' }]}
                            >
                                <Input
                                    prefix={<MailOutlined className="site-form-item-icon text-gray-400" rev={undefined} />}
                                    type='email'
                                    name='email'
                                    value={email}
                                    placeholder="Entrez votre email"
                                    onChange={handleChange}
                                    className="rounded-lg py-2"
                                />
                            </Form.Item>
                            <Form.Item
                                label={<label className='text-gray-700 font-medium'>Mot de passe</label>}
                                name="password"
                                rules={[{ required: true, message: 'Veuillez entrer votre mot de passe!' }]}
                            >
                                <Input
                                    prefix={<LockOutlined className="site-form-item-icon text-gray-400" rev={undefined} />}
                                    type="password"
                                    name='password'
                                    value={password}
                                    placeholder="Entrez votre mot de passe"
                                    onChange={handleChange}
                                    className="rounded-lg py-2"
                                />
                            </Form.Item>

                            <div className="flex justify-end mb-4">
                                <Link to="/auth/forgot-password"
                                    onClick={setForgotPassword}
                                    className="text-blue-600 hover:text-blue-800 text-sm cursor-pointer"
                                >
                                    Mot de passe oublié?
                                </Link>
                            </div>

                            <Form.Item>
                                <Button
                                    size='large'
                                    htmlType="submit"
                                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg h-12 font-medium"
                                    disabled={loading}
                                >
                                    {loading ? <Spin /> : 'Se connecter'}
                                </Button>
                            </Form.Item>

                            <AnimatePresence>
                                {error && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="mb-6"
                                    >
                                        {typeof error === 'string' ? (
                                            <Alert 
                                                message="Erreur" 
                                                description={error} 
                                                type="error" 
                                                showIcon 
                                                className="text-left"
                                            />
                                        ) : (
                                            error
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <div className="text-center mb-6">
                                <p className="text-gray-600">
                                    Vous n'avez pas de compte?
                                    <Link
                                        to={"/auth/register"}
                                        className="text-blue-600 hover:text-blue-800 ml-1 cursor-pointer font-medium"
                                        onClick={handleToggleForm}
                                    >
                                        Créer un compte
                                    </Link>
                                </p>
                            </div>

                            <Divider plain className="text-gray-500">ou connectez-vous avec</Divider>

                            <motion.div 
                                className="mt-4"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <Button
                                    onClick={handleGoogleLogin}
                                    className="w-full bg-white border border-gray-300 rounded-lg h-12 font-medium flex items-center justify-center"
                                    disabled={loading}
                                >
                                    {loading ? <Spin /> : (
                                        <div className='flex items-center justify-center'>
                                            <FcGoogle className="mr-2" size={20} />
                                            Se connecter avec Google
                                        </div>
                                    )}
                                </Button>
                            </motion.div>
                        </Form>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default Login;
